import React, { useState, useEffect } from 'react'
import { connect as connectRedux } from 'react-redux'
import compose from 'recompose/compose'
import _size from 'lodash/size'
import _trim from 'lodash/trim'
import FormsyForm from 'formsy-react'
import Button, { TYPES as BUTTON_TYPES } from '../../Button/index'
import { connect as connectStaticText } from '../../StaticText/connect'
import { Textarea } from '../../FormInput.v2/index'
import ArrowRightIcon from '../../Icon/icons/ArrowRight/index'
import ChevronRightIcon from '../../Icon/icons/ChevronRight/index'
import { ICON_COLORS } from '../../Icon/index'
import { URL_AI_CHAT, URL_ACCOUNT_CANCEL_CONFIRM } from '../../../services/url/constants'
import { historyRedirect } from '../../../services/navigation/index'
import {
  AI_MESSAGE_SENT_GA4_EVENT,
  AI_CONVERSATION_STARTED_GA4_EVENT,
} from '../../../services/event-tracking/index'
import { getUserLanguagePrimary } from '../../../services/user/index'
import { executeUserAccountCancel } from '../../../services/user-account/index'
import { EN } from '../../../services/languages/constants'
import { DE as COUNTRY_DE } from '../../../services/country/index'
import { getBoundActions } from '../../../actions/index'
import { usePostHogEvent, AI_INPUT_METHOD } from '../../PostHog/index'

const formName = 'userPrompt'
const KEYBOARD_KEYS = {
  ENTER: 'Enter',
}

/**
 * Cancel modal with AI options
 * @returns {JSX.Element} Cancel AI modal
 */
function MyAccountCancelAIModal (props) {
  const {
    onCancel,
    oracle,
    staticText,
    auth,
    user,
    language,
    setAiGa4Event,
  } = props
  const oraclePersonalityId = oracle.get('persona')
  const [promptValue, setPromptValue] = useState('')
  const trackCancelModalViewed = usePostHogEvent('aiCancelModalViewed')
  const trackCancelModalEngaged = usePostHogEvent('aiCancelModalEngaged')
  const trackCancelModalCancelClicked = usePostHogEvent('aiCancelModalCancelClicked')
  const userLanguage = getUserLanguagePrimary(user) || language || EN
  const isGermanUser = user.getIn(['data', 'requestCountry']) === COUNTRY_DE

  // Track modal view event when component mounts. The PostHog hook now fans
  // out to GA4 too (AI-820 PR2) — no separate setAiGa4Event call needed.
  useEffect(() => {
    trackCancelModalViewed()
  }, [])

  // Track engagement events when user submits a prompt
  const handleSubmit = (formData) => {
    const userPrompt = formData[formName]

    if (!userPrompt || _size(userPrompt) === 0) {
      return
    }

    // Engagement: the PostHog hook fans out to GA4 too (AI-820 PR2), carrying
    // ui_element + input_method on both legs. ai_persona is filled by each
    // pipeline's default (GA4 middleware reads oracle.persona).
    trackCancelModalEngaged({ inputMethod: AI_INPUT_METHOD.TEXT, uiElement: 'Text Field' })
    setAiGa4Event(AI_MESSAGE_SENT_GA4_EVENT.mergeDeep({
      ui_element: 'Text Field',
      input_method: AI_INPUT_METHOD.TEXT,
      ai_persona: oraclePersonalityId,
    }))
    setAiGa4Event(AI_CONVERSATION_STARTED_GA4_EVENT.mergeDeep({
      ui_element: 'Text Field',
      ai_persona: oraclePersonalityId,
    }))

    historyRedirect({
      history: props.history,
      url: URL_AI_CHAT,
      query: { prompt: userPrompt, oraclePersonalityId },
      auth,
      language: userLanguage,
    })
  }

  const onChange = (value) => {
    setPromptValue(value)
  }

  const handleKeyDown = (event) => {
    // If Enter is pressed without Shift, submit the form
    if (event.key === KEYBOARD_KEYS.ENTER && !event.shiftKey) {
      event.preventDefault()

      // Only submit if there's content
      if (promptValue && _size(_trim(promptValue)) > 0) {
        handleSubmit({ [formName]: promptValue })
      }
    }
  }

  const renderCancelWithOptions = () => {
    return (
      <div className="my-account-cancel-ai-modal__cancel-link my-account-cancel-ai-modal__cancel-link--de">
        <div className="my-account-cancel-ai-modal__cancel-options">
          <Button
            type={BUTTON_TYPES.PRIMARY}
            onClick={() => {
              trackCancelModalCancelClicked()
              onCancel()
            }}
            className="my-account-cancel-ai-modal__cancel-options-button"
          >
            {staticText.getIn(['data', 'walkThroughOptions'])}
          </Button>
        </div>
        <Button
          type={BUTTON_TYPES.LINK}
          onClick={() => executeUserAccountCancel({
            props,
            historyRedirect,
            userLanguage,
            cancelConfirmUrl: URL_ACCOUNT_CANCEL_CONFIRM,
          })}
          className="my-account-cancel-ai-modal__cancel-link-button"
        >
          {staticText.getIn(['data', 'cancelNow'])}
        </Button>
      </div>
    )
  }

  const renderCancelLink = () => {
    return (
      <div className="my-account-cancel-ai-modal__cancel-link">
        <Button
          type={BUTTON_TYPES.LINK}
          onClick={() => {
            trackCancelModalCancelClicked()
            onCancel()
          }}
          className="my-account-cancel-ai-modal__cancel-link-button"
        >
          {staticText.getIn(['data', 'cancelLink'])}
          <ChevronRightIcon size="20" />
        </Button>
      </div>
    )
  }

  return (
    <div className="my-account-cancel-ai-modal">
      <div className="my-account-cancel-ai-modal__intro">
        {staticText.getIn(['data', 'introText'])}
      </div>
      <div className="my-account-cancel-ai-modal__title">
        {staticText.getIn(['data', 'title'])}
      </div>
      <div className="my-account-cancel-ai-modal__description">
        {staticText.getIn(['data', 'description'])}
      </div>
      <FormsyForm onValidSubmit={handleSubmit} className="my-account-cancel-ai-modal__form">
        <div className="my-account-cancel-ai-modal__input-wrapper">
          <Textarea
            className="my-account-cancel-ai-modal__input"
            name={formName}
            label=""
            hideLabel
            placeholder={staticText.getIn(['data', 'placeholder'])}
            value={promptValue}
            maxLength={1001}
            validations="maxLength:1000"
            onChange={onChange}
            onKeyDown={handleKeyDown}
          />
          <Button
            type={BUTTON_TYPES.DEFAULT}
            submit={!!promptValue}
            className="my-account-cancel-ai-modal__submit-button"
          >
            <ArrowRightIcon color={ICON_COLORS.WHITE} />
          </Button>
        </div>
      </FormsyForm>
      {isGermanUser ? renderCancelWithOptions() : renderCancelLink()}
    </div>
  )
}

export default compose(
  connectStaticText({ storeKey: 'myAccountCancelAIModal' }),
  connectRedux(
    (state) => ({
      oracle: state.oracle,
      auth: state.auth,
      user: state.user,
      userAccount: state.userAccount,
      language: state.language,
    }),
    (dispatch) => {
      const actions = getBoundActions(dispatch)
      return {
        setAiGa4Event: actions.eventTracking.setAiGa4Event,
        setUserAccountCancelReason: actions.userAccount.setUserAccountCancelReason,
        setUserAccountCancelFormData: actions.userAccount.setUserAccountCancelFormData,
        setUserAccountCancel: actions.userAccount.setUserAccountCancel,
      }
    },
  ),
)(MyAccountCancelAIModal)
